import * as React from 'react';

import { ClassroomInterface, SkillPracticeRecommendation, SkillPracticeAssignment } from './interfaces';

import { requestPost } from '../../../../../modules/request';
import useModalAccessibility from '../../../../Shared/hooks/useModalAccessibility';
import {
  RadioButton,
  Tooltip,
  pluralize,
  titleCaseMultiWordString,
} from '../../../../Shared/index';
import numberSuffixBuilder from '../../modules/numberSuffixBuilder';
import DemoGuardedButton from '../../shared/demoGuardedButton';

interface AssignTargetedPracticeModalProps {
  assignmentSmartGroup: string | null;
  assignmentStudentIds: number[];
  classroom: ClassroomInterface;
  closeModal: (snackbarMessage?: string) => void;
  getStudentAssignmentData: (selectedPackId: number) => {
    alreadyAssignedStudentIds: number[],
    newAssignmentStudentIds: number[],
    alreadyAssignedStudents: any[],
    newAssignmentStudents: any[],
    allSelectedStudents: any[]
  };
  skillPracticeRecommendations: SkillPracticeRecommendation[];
  locationPathname?: string;
}

const AssignTargetedPracticeModal = ({
  assignmentSmartGroup,
  assignmentStudentIds,
  classroom,
  closeModal,
  skillPracticeRecommendations,
  getStudentAssignmentData,
  locationPathname = window.location.pathname
}: AssignTargetedPracticeModalProps) => {
  const { modalRef } = useModalAccessibility(closeModal);
  // Default to the pack with the lowest order number
  const defaultPackId = React.useMemo(() => {
    if (skillPracticeRecommendations.length === 0) return null;
    const sortedByOrder = [...skillPracticeRecommendations].sort((a, b) => a.order_number - b.order_number);
    return sortedByOrder[0].unit_template_id;
  }, [skillPracticeRecommendations]);
  const [selectedPackId, setSelectedPackId] = React.useState<number | null>(defaultPackId);
  const [isAssigning, setIsAssigning] = React.useState<boolean>(false);

  const selectedPack = skillPracticeRecommendations.find(rec => rec.unit_template_id === selectedPackId);
  const hasMoreThanOnePack = skillPracticeRecommendations.length > 1;

  function getSectionFromUrl(): string | null {
    const currentPath = locationPathname;
    if (currentPath.includes('smart_groups/by_skill')) {
      return 'Smart Groups | By Skill';
    } else if (currentPath.includes('smart_groups/by_student')) {
      return 'Smart Groups | By Student';
    } else if (currentPath.includes('performance_breakdown/by_student')) {
      return 'Performance Breakdown | By Student';
    } else {
      return null;
    }
  }

  function titleCopy() {
    if (assignmentSmartGroup) {
      return 'Assign targeted practice to Smart Group'
    }

    if (assignmentStudentIds.length === classroom.students.length) {
      return 'Assign targeted practice to class'
    }

    if (assignmentStudentIds.length === 1) {
      return 'Assign targeted practice to student'
    }

    return 'Assign targeted practice to selected students'
  }

  function handleClickAssign(): void {
    const { newAssignmentStudentIds, newAssignmentStudents } = getStudentAssignmentData(selectedPackId);

    if (newAssignmentStudents.length === 0) {
      // No students can be assigned because they all already have this pack
      const snackbarMessage = `The ${selectedPack?.unit_template_name} activity pack has already been assigned.`;
      closeModal(snackbarMessage);
    } else {
      setIsAssigning(true);

      requestPost(
        '/teachers/progress_reports/assign_single_pack',
        {
          classroom_id: classroom.id,
          unit_template_id: selectedPackId,
          student_ids: newAssignmentStudentIds
        },
        () => {
          setIsAssigning(false);

          requestPost(
            '/teachers/progress_reports/track_teacher_assigned_targeted_practice',
            {
              activity_pack_name: selectedPack?.unit_template_name,
              report: 'Skills Report',
              section: getSectionFromUrl()
            }
          );

          const snackbarMessage = 'Targeted practice assigned. You can view the assignment in Manage Activities.';
          closeModal(snackbarMessage);
        },
        (error) => {
          setIsAssigning(false);
          const snackbarMessage = 'Failed to assign targeted practice. Please try again.';
          closeModal(snackbarMessage);
        }
      );
    }
  }

  function getStudentName(): string {
    if (assignmentStudentIds.length === 1) {
      const student = classroom.students?.find(s => s.id === assignmentStudentIds[0]);
      return student ? student.name : 'Student';
    }
    return '';
  }

  function getReadabilityRange(): string {
    if (selectedPack.min_readability === selectedPack.max_readability) {
      return `${numberSuffixBuilder(selectedPack.min_readability)} grade`;
    }
    return `${numberSuffixBuilder(selectedPack.min_readability)} grade to ${numberSuffixBuilder(selectedPack.max_readability)} grade`;
  }

  function getAllSelectedStudentNames(): string {
    return getStudentAssignmentData(selectedPackId).allSelectedStudents.map(student => student.name).join(', ');
  }

  function getNewAssignmentStudentNames(): string {
    return getStudentAssignmentData(selectedPackId).newAssignmentStudents.map(student => student.name).join(', ');
  }

  function getAlreadyAssignedStudentNames(): string {
    return getStudentAssignmentData(selectedPackId).alreadyAssignedStudents.map(student => student.name).join(', ');
  }

  function renderStudentCountTooltip(count: number, studentNames: string): React.ReactElement {
    let studentCount = <strong>{count} {pluralize(count, 'student', 'students')}</strong>

    if (count === 0) {
      return studentCount
    }

    if (count === classroom.students.length) {
      studentCount = <strong>All students</strong>
    }

    return (
      <Tooltip
        tooltipText={studentNames}
        tooltipTriggerText={studentCount}
      />
    );
  }

  function renderAssignmentDescription() {
    if (hasMoreThanOnePack) {
      return (
        <React.Fragment>
          The selected activity pack includes <strong>{selectedPack.activity_count} {pluralize(selectedPack.activity_count, 'activity', 'activities')}</strong>,{' '}
          {selectedPack.min_readability === selectedPack.max_readability ? (
            <React.Fragment>all at a <strong>{getReadabilityRange()}</strong> readability level.</React.Fragment>
          ) : (
            <React.Fragment>with readability levels ranging from <strong>{getReadabilityRange()}</strong>.</React.Fragment>
          )}
          {' '}You can preview the activities <a href={`/assign/featured-activity-packs/${selectedPack.unit_template_id}`} rel="noopener noreferrer" target="_blank">here</a>.
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          You're about to assign <strong>{selectedPack.unit_template_name}</strong>
          {assignmentStudentIds.length === 1 && ` to ${getStudentName()}`}.
          {' '}This activity pack includes <strong>{selectedPack.activity_count} {pluralize(selectedPack.activity_count, 'activity', 'activities')}</strong>,{' '}
          {selectedPack.min_readability === selectedPack.max_readability ? (
            <React.Fragment>all at a <strong>{getReadabilityRange()}</strong> readability level.</React.Fragment>
          ) : (
            <React.Fragment>with readability levels ranging from <strong>{getReadabilityRange()}</strong>.</React.Fragment>
          )}
          {' '}You can preview the activities <a href={`/assign/featured-activity-packs/${selectedPack.unit_template_id}`} rel="noopener noreferrer" target="_blank">here</a>.
        </React.Fragment>
      );
    }
  }

  function getAssignButtonText(): string {
    if (isAssigning) {
      return 'Assigning...';
    }

    if (assignmentStudentIds.length === 1) {
      return 'Assign';
    }

    const { newAssignmentStudents } = getStudentAssignmentData(selectedPackId);

    if (newAssignmentStudents.length === classroom.students.length) {
      return 'Assign to all students'
    }

    return `Assign to ${newAssignmentStudents.length} ${pluralize(newAssignmentStudents.length, 'student', 'students')}`;
  }

  function renderAssignmentSummary() {
    const { newAssignmentStudents, alreadyAssignedStudents } = getStudentAssignmentData(selectedPackId);

    if (alreadyAssignedStudents.length > 0) {
      return (
        <p>
          {renderStudentCountTooltip(newAssignmentStudents.length, getNewAssignmentStudentNames())}
          {assignmentSmartGroup ? ` in the '${titleCaseMultiWordString(assignmentSmartGroup)}' Smart Group` :
            assignmentStudentIds.length === classroom.students?.length ? ` in ${classroom.name}` : ''}
          {' '}will be assigned practice.<br /><br />{renderStudentCountTooltip(alreadyAssignedStudents.length, getAlreadyAssignedStudentNames())} already received this practice and won't be re-assigned.
        </p>
      );
    }

    if (assignmentSmartGroup) {
      return (
        <p>
          The {renderStudentCountTooltip(assignmentStudentIds.length, getAllSelectedStudentNames())} in the '{titleCaseMultiWordString(assignmentSmartGroup)}' Smart Group will be assigned practice.
        </p>
      );
    }

    return (
      <p>
        {renderStudentCountTooltip(assignmentStudentIds.length, getAllSelectedStudentNames())} will be assigned practice.
      </p>
    );
  }

  return (
    <div className="modal-container assign-targeted-practice-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="AssignTargetedPracticeModal"
        aria-modal="true"
        className="assign-targeted-practice-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="title">
          <h2 id="AssignTargetedPracticeModal">{titleCopy()}</h2>
        </div>

        {hasMoreThanOnePack && (
          <div className="pack-selection-section-wrapper">
            <h3>Select activity pack</h3>
            <div className="pack-selection-section">
              <p>Please select an activity pack:</p>
              <div className="radio-options">
                {[...skillPracticeRecommendations]
                  .sort((a, b) => a.order_number - b.order_number)
                  .map((rec) => (
                    <RadioButton
                      key={rec.unit_template_id}
                      label={rec.unit_template_name}
                      onClick={() => setSelectedPackId(rec.unit_template_id)}
                      selected={selectedPackId === rec.unit_template_id}
                      value={rec.unit_template_id}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="assignment-details-section">
          {hasMoreThanOnePack && <h3>Review assignment details</h3>}
          <p>
            {renderAssignmentDescription()}
          </p>
          {assignmentStudentIds.length > 1 && (
            <div className="assignment-summary">
              {renderAssignmentSummary()}
            </div>
          )}
          <a className="learn-more-link" href="https://support.quill.org/en/articles/12062553-how-do-i-read-the-skills-report-quill-premium" rel="noopener noreferrer" target="_blank">Learn more about targeted practice</a>
        </div>
        <div className="form-buttons">
          <button className="quill-button outlined medium grey focus-on-light" disabled={isAssigning} onClick={() => closeModal()} type="button">Cancel</button>
          <DemoGuardedButton
            className='quill-button contained medium green focus-on-light'
            disabled={!selectedPack || isAssigning}
            onClick={handleClickAssign}
          >
            {getAssignButtonText()}
          </DemoGuardedButton>
        </div>
      </div>
    </div>
  )
}

export default AssignTargetedPracticeModal
